import _ from 'lodash'
import EventEmitter from 'events'
import * as TimSort from 'timsort'
import locale from 'scoreboard/components/locale'
import { Writable } from 'stream'

function isReadableStream(test) {
    return test instanceof EventEmitter && typeof test.read === 'function'
}

function resolveSensitivity(opts) {
	if (opts.accentSensitive && opts.caseSensitive) {
		return 'variant'
	} else if (opts.caseSensitive) {
		return 'case'
	} else if (opts.accentSensitive) {
		return 'accent'
	}
	return 'base'
}

class SortSink extends Writable {
	constructor(options = {array: []}) {
		super(options)
		this.array = options.array
	}

	_write(chunk, enc, next) {
		if (_.isArray(chunk)) {
			this.array = this.array.concat(chunk)
		} else {
			this.array.push(chunk)
		}
		next()
	}

	array () {
		return this.array
	}
}

//the numeric parameter will try to sort string numbers by their numerical value
//e.g.  "1", "2", 10" instead of "1", "10", "2".
const DEFAULT_COLLATOR_OPTIONS = {
	numeric: true,
	sensitivity: 'variant'
}

function doCompare(a, b, compare, toVal) {
	let a1 = toVal ? toVal(a) : a
	let b1 = toVal ? toVal(b) : b
	return compare(a1, b1)
}

const DEFAULT_SORT_OPTIONS = {
	nilsFirst: false,
	caseSensitive: true,
	accentSensitive: true,
	alphanumeric: true,
	reversed: false,
	emptyAsNil: true,
	compare: null,
	compound: null,
	toValue: null
}

/**
 * Sorts a collection of objects. String values are sorted by their locale
 * dependant collation.
 */
class Sorter extends EventEmitter {
	/**
	 * Creates the Sorter
	 *
	 * @param {boolean} [opts.nilsFirst=false] - true if we put null and
	 * undefined values at the beginning of the collection
	 * @param {boolean} [opts.caseSensitive=true] - true if upper and lower case
	 * characters are treated as different
	 * @param {boolean} [opts.accentSensitive=true] - true if accent characters
	 * are treated as different
	 * @param {boolean} [opts.alphanumeric=true] - true if the collection contains
	 * both strings and numbers.  If sorting only numbers callers should set this
	 * to false for faster performance
	 * @param {boolean} [opts.reversed=false] - true if the order returned is
	 * reverse to the collection's natural ordering (e.g. descending)
	 * @param {boolean} [opts.emptyAsNil=true] - true if we treat empty strings
	 * as null
	 * @param {Object | Function} [opts.nilsAs] - if defined treats all nils as whatever
	 * the value returned by the given value/function.  If defined the nilsFirst
	 * setting is ignored
	 * @param {Function} [opts.compare=null] - custom compare function to sort
	 * the item by, function will be passed two strings and expects to return
	 * a negative value if string1 comes before string2; a positive value if
	 * string1 comes after string2; 0 if they are considered equal.
	 * @param {Array} [opts.compound=null] - if defined expects an array
	 * of comparing functions to handle ties for compound ordering that are invoked
	 * sequentially.  If none provided the default behavior to handle ties is to
	 * keep the order the same
	 * @param {Function} [opts.toValue=null] - function to extract the value used for
	 * the sort from the item under comparison. The default behavior uses the item itself
	 * @param {Object} [opts.collator=null] - A collator to use for alphanumeric sorting
	 */
	constructor(opts = {}) {
		super()
		this.opts = _.assign({}, DEFAULT_SORT_OPTIONS, opts)
		this._resolveCollator()
	}

	_resolveCollator() {
		if (this.opts.collator) {
			this.collator = this.opts.collator
		} else {
			let collatorOpts = _.defaults({sensitivity: resolveSensitivity(this.opts)}, DEFAULT_COLLATOR_OPTIONS)
			this.collator = new Intl.Collator(locale().locale, collatorOpts)
		}
	}

	options(opts = null, override = false) {
		if (opts == null) {
			return _.clone(this.opts)
		}
		if (override) {
			_.extend(this.opts, _.cloneDeep(opts))
		} else {
			Object.keys(opts).forEach((prop) => {
				this.opts[prop] = !_.isNil(this.opts[prop]) ? !!opts[prop] : this.opts[prop]
			})
		}
		//if the opts change, update the collator
		this._resolveCollator()
		return this
	}

	isNil(a) {
		let a1 = a === null || a === undefined
		if (this.opts.emptyAsNil) {
			if (!a1 && a.toString().trim().length === 0) {
				return true
			}
		}
		return a1
	}

	compare(a, b) {
		let a1 = this.isNil(a)
		let b1 = this.isNil(b)

		if (a1 && b1) return 0

		var val
		if (!_.isNil(this.opts.nilsAs)) {
			let nilsAs = _.result(this.opts, 'nilsAs')
			if (a1) {
				val = nilsAs
			}
			if (b1) {
				val = nilsAs
			}
		} else {
			if (a1 && this.opts.nilsFirst || b1 && !this.opts.nilsFirst) {
				return -1
			} else if (a1 && !this.opts.nilsFirst || b1 && this.opts.nilsFirst) {
				return 1
			}
		}

		if (!this.opts.alphanumeric) {
			//when only dealing with numbers this is way faster
			val = a - b
		} else {
			val = this.collator.compare(a, b)
		}

		return this.opts.reversed ? val * -1 : val
	}

	/**
	 * Sorts a given array. The array passed in is not mutated.
	 *
	 * @param {Array} items - the array to apply the sort to
	 * @param {Stream} [stream] - applies the sort over the concatenation of the
	 * array and the contents of the stream.
	 * @param {Function} [valueExtractor] - function should return the value used
	 * to compare the order that the items should be sorted by
	 * @returns {Array} A new array sorted by the options defined. Callers that
	 * supply a stream should listen for a 'done' event emitted by the Sorter
	 * for the sorted array instead of relying on the returned array.
	 */
	sort(items = [], stream, valueExtractor) {
		//clone the incoming array
		let arr = items.slice()

		var extractor = this.opts.toValue
		if (arguments.length > 1) {
			if (isReadableStream(stream)) {
				if (!stream.endEmitted) {
					let mode = stream._readableState.objectMode
					let sink = new SortSink({objectMode: mode, array: arr})
					sink.on('finish', () => {
						this.sort(sink.array, valueExtractor)
					})
					stream.pipe(sink)
					if (stream.next) {
						var done = stream.endEmitted
						stream.on('end', () => done = true)
						do {
							stream.next()
						} while (!done)
					}
					return this
				}
			} else {
				valueExtractor = stream
			}
		}
		if (valueExtractor) {
			extractor = valueExtractor
			if (_.isString(valueExtractor)) {
				extractor = obj => obj[valueExtractor]
			} else if (!_.isFunction(valueExtractor)) {
				let key = valueExtractor['sortField']
				extractor = obj => obj[key]
				let setAscending = _.get(valueExtractor, 'ascending', null)
				if (setAscending != null) {
					this.options({reversed: !setAscending})
				}
			}
		}

		let compare = this.compare.bind(this)
		if (this.opts.compare) {
			compare = (a, b) => {
				return this.opts.compare(a, b, this.opts)
			}
		}

		//faster than Array.sort https://github.com/mziccard/node-timsort#performance
		//also default algorithm in Java & Python
		TimSort.sort(arr, (a, b) => {
			let value = doCompare(a, b, compare, extractor)

			//implement compound ordering for #15374
			let compoundOrdering = this.opts.compound
			if (value === 0 && compoundOrdering) {
				if (!_.isArray(compoundOrdering)) {
					compoundOrdering = new Array(compoundOrdering)
				}
				_.some(compoundOrdering, comparator => {
					let comparing = comparator.compare.bind(comparator)
					let toValue = _.get(comparator, 'opts.toValue')
					value = doCompare(a, b, comparing, toValue)
					return value == 0 ? false : true
				})
			}
			return value
		})
		this.results = arr
		this.emit('done', arr)
		return this
	}

	thenBy(valueExtractor = null, opts = null) {
		if (!this.results) return this
		if (opts != null) this.opts = _.assign(this.opts, opts)
		return this.sort(this.results, valueExtractor)

	}
}

function defaultCollatorOptions () {
	return _.cloneDeep(DEFAULT_COLLATOR_OPTIONS)
}

function defaultSortOptions () {
	return _.cloneDeep(DEFAULT_SORT_OPTIONS)
}

function nilCompare (nilsFirst) {
	return (a,b) => {
		let aNil = _.isNil(a)
		let bNil = _.isNil(b)
		if (aNil && bNil) {
			return 0
		} else if (aNil && nilsFirst || bNil && !nilsFirst) {
			return -1
		} else if (aNil && !nilsFirst || bNil && nilsFirst) {
			return 1
		}
		return null
	}
}

export default Sorter
export { defaultCollatorOptions, defaultSortOptions, nilCompare }
Sorter.__filename__ = 'components/sorter.js'
